import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api'; 
import FullScreenLoader from '../components/Loader';
import * as XLSX from 'xlsx';
import { useParams } from 'react-router-dom';
import Container from '../components/Container';

const MessageHistory = () => {
  const { campaign_id } = useParams();

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(''); 
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [exporting, setExporting] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [exportStatus, setExportStatus] = useState('all');
  const itemsPerPage = 20; 

  useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedSearch(searchTerm);
        setCurrentPage(1);
      }, 500); 
  
      return () => clearTimeout(handler);
    }, [searchTerm]);

const fetchHistory = useCallback(async () => {
  try {
    setLoading(true);
    const res = await api.get(`/whatsapp/message-history`, {
      params: {
        campaign_id: campaign_id,
        page: currentPage,
        limit: itemsPerPage,
        status: exportStatus !== 'all' ? exportStatus : undefined,
        search: debouncedSearch 
      }
    }); 
    
    if (res.status === 200) {
      setHistory(res.data.messages_history || []);
      setTotalPages(res.data.totalPages || 1);
    }
  } catch (err) {
    console.error("History Fetch Error:", err);
  } finally {
    setLoading(false);
  }
}, [campaign_id, currentPage, debouncedSearch, exportStatus]); 

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);


  const deleteHistory = async () => {
    const isConfirmed = window.confirm("Are you sure you want to clear message history?");
    
    if (!isConfirmed) return;

    try {
      setLoading(true);
      await api.get('/whatsapp/delete-history', {
        params: {
          page: 1,
          limit: 10000,
          status: exportStatus !== 'all' ? exportStatus : undefined,
          search: debouncedSearch 
        }
      });
      fetchHistory();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const res = await api.get('/whatsapp/message-history', {
        params: {
          page: 1,
          limit: 10000,
          status: exportStatus !== 'all' ? exportStatus : undefined,
          search: debouncedSearch 
        }
      });
      
      const allData = res.data.messages_history || [];
      if (allData.length === 0) return alert("No data to export");

      const worksheet = XLSX.utils.json_to_sheet(allData.map(item => ({
        "Date": new Date(item.created_at).toLocaleString(),
        "Receiver": item.phone,
        "Message": item.message,
        "Status": item.status.toUpperCase()
      })));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "History");
      XLSX.writeFile(workbook, `History_${exportStatus}.xlsx`);
    } catch (err) {
      console.error(err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container title={'Broadcast Message History'} deleteButton={true} onDeleteClick={deleteHistory} rightSection={
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <select value={exportStatus} onChange={(e) => setExportStatus(e.target.value)} style={selectStyle}>
            <option value="all">All Status</option>
            <option value="read">Only Read</option>
            <option value="delivered">Only Delivered</option>
            <option value="sent">Only Sent</option>
            <option value="pending">Only Pending</option>
            <option value="failed">Only Failed</option>
          </select>
          <button onClick={handleExportExcel} style={exportBtn}>📊 Export</button>
        </div>
      } onClick={fetchHistory}>

      {(loading || exporting) && <FullScreenLoader />}

      <div style={searchContainer}>
        <input 
          type="text" 
          placeholder="Search by number or message or status etc..." 
          style={searchInput}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {loading && <small style={{color: '#666'}}>Searching database...</small>}
      </div>

      <div style={tableWrapper}>
        <table style={tableStyle}>
          <thead>
            <tr style={thRow}>
              <th style={thStyle}>Id</th>
              <th style={thStyle}>Date & Time</th>
              <th style={thStyle}>Receiver</th>
              <th style={thStyle}>Sender</th>
              <th style={thStyle}>Message</th>
              <th style={thStyle}>Failed Message</th>
              <th style={thStyle}>Broadcast Type</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {history.length > 0 ? (
              history.map((log, index) => (
                <tr key={index} style={trStyle}>
                  <td style={tdStyle}>{log.id}</td>
                  <td style={tdStyle}>{new Date(log.created_at).toLocaleString()}</td>
                  <td style={tdStyle}><strong>{log.phone}</strong></td>
                  <td style={tdStyle}>{log.account_id ?? 'N/A'}</td>
                  <td title={log.message} style={{ ...tdStyle, maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.message}
                  </td>
                  <td title={log.failed_message} style={{ ...tdStyle, maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.failed_message || 'N/A'}
                  </td>
                  <td title={log.type} style={{ ...tdStyle, maxWidth: '100px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {log.type ? 'Whatsapp Template' : 'Whatsapp Login'}
                  </td>
                  <td style={tdStyle}>
                    <span style={statusBadge(log.status)}>
                      {log.status === 'read' ? '👀 Read' : 
                       log.status === 'delivered' ? '📬 Delivered' : 
                       log.status === 'sent' ? '✅ Sent' : 
                       log.status === 'pending' ? '⏳ Pending' : 
                       '❌ Failed'}
                    </span>
                  </td>
                </tr>
              ))
            ) : (
              <tr><td colSpan="7" style={{ textAlign: 'center', padding: '30px' }}>No records found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={paginationStyle}>
        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} style={pageBtn}>Prev</button>
        <span style={{ margin: '0 15px' }}>Page {currentPage} of {totalPages}</span>
        <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)} style={pageBtn}>Next</button>
      </div>
    </Container>
  );
};

const searchContainer = { marginBottom: '20px' };
const searchInput = { width: '97%', padding: '12px 20px', borderRadius: '8px', border: '1px solid #ddd' };
const tableWrapper = { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', overflow: 'hidden' };
const tableStyle = { width: '100%', borderCollapse: 'collapse' };
const thRow = { backgroundColor: '#f1f3f5' };
const thStyle = { padding: '15px', textAlign: 'left', borderBottom: '2px solid #eee' };
const tdStyle = { padding: '15px', borderBottom: '1px solid #f0f0f0' };
const trStyle = { transition: '0.2s' };
const paginationStyle = { display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '30px' };
const pageBtn = { padding: '8px 20px', cursor: 'pointer', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '5px' };
const exportBtn = { padding: '10px 20px', backgroundColor: '#007bff', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' };
const selectStyle = { padding: '10px', borderRadius: '6px', border: '1px solid #ddd' };

// Refactored status badge logic for all 5 states
const statusBadge = (status) => {
  let bgColor = '#fee2e2'; // Failed (Red)
  let textColor = '#991b1b'; 
  
  if (status === 'read') {
    bgColor = '#f3e8ff'; // Read (Purple)
    textColor = '#6b21a8';
  } else if (status === 'delivered') {
    bgColor = '#e0f2fe'; // Delivered (Blue)
    textColor = '#075985';
  } else if (status === 'sent') {
    bgColor = '#dcfce7'; // Sent (Green)
    textColor = '#166534';
  } else if (status === 'pending') {
    bgColor = '#fef3c7'; // Pending (Yellow)
    textColor = '#92400e';
  }

  return {
    padding: '4px 10px', 
    borderRadius: '12px', 
    fontSize: '12px', 
    fontWeight: '600', 
    backgroundColor: bgColor, 
    color: textColor 
  };
};

export default MessageHistory;